import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import classnames from 'classnames';
import checkIcon from '../assets/images/check-blue.svg';
import closeIcon from '../assets/images/close.svg';
import { useStoreState, useStoreActions } from '../store';
import { useLocale } from '../hooks/useLocale';
import { SubtitleTextSizes, SubtitleTextWeights, SubtitleTextColors } from '../typings';
import { Toggle } from './Toggle';
import { theme } from '../utils/theme';

const textSizeOptions = [
  SubtitleTextSizes.Smallest,
  SubtitleTextSizes.Small,
  SubtitleTextSizes.Normal,
  SubtitleTextSizes.Large,
  SubtitleTextSizes.Largest,
];

const textColorOptions = [
  SubtitleTextColors.White,
  SubtitleTextColors.Gray,
  SubtitleTextColors.Black,
  SubtitleTextColors.Yellow,
  SubtitleTextColors.Orange,
  SubtitleTextColors.Red,
  SubtitleTextColors.Green,
  SubtitleTextColors.Cyan,
  SubtitleTextColors.Blue,
];

export const SubtitleOptions = () => {
  const locale = useLocale();
  const controlsVisible = useStoreState(state => state.player.controlsVisible);
  const subtitleOptionsVisible = useStoreState(state => state.player.subtitleOptionsVisible);
  const subtitleTextSize = useStoreState(state => state.subtitles.subtitleTextSize);
  const subtitleTextColor = useStoreState(state => state.subtitles.subtitleTextColor);
  const subtitleTextWeight = useStoreState(state => state.subtitles.subtitleTextWeight);
  const actions = useStoreActions(actions => actions);
  const [visible, setVisible] = React.useState(false);
  const [transitioned, setTransitioned] = React.useState(false);

  function handleSizeClick(e: React.MouseEvent, size: SubtitleTextSizes) {
    e.stopPropagation();
    actions.subtitles.setSubtitleTextSize(size);
    actions.settings.setSubtitleTextSize(size);
  }

  function handleCloseClick() {
    actions.player.setSubtitleOptionsVisible(false);
  }

  function handleBackdropPress() {
    actions.player.setSubtitleOptionsVisible(false);
  }

  function handleWeightChange() {
    const { Bold, Regular } = SubtitleTextWeights;
    const weight = subtitleTextWeight === Bold ? Regular : Bold;
    actions.subtitles.setSubtitleTextWeight(weight);
    actions.settings.setSubtitleTextWeight(weight);
  }

  function handleColorClick(colorOption: SubtitleTextColors) {
    actions.subtitles.setSubtitleTextColor(colorOption);
    actions.settings.setSubtitleTextColor(colorOption);
  }

  function handleAnimationEnd() {
    setVisible(controlsVisible && subtitleOptionsVisible);
  }

  // Delay component unmount during animation
  React.useEffect(() => {
    const isVisible = controlsVisible && subtitleOptionsVisible;
    setTransitioned(isVisible);
    setTimeout(() => setVisible(isVisible), isVisible ? 0 : 300);
  }, [controlsVisible, subtitleOptionsVisible]);

  return visible ? (
    <>
      <Backdrop onClick={handleBackdropPress} />
      <Container className={classnames(transitioned ? 'visible' : 'hidden')} onAnimationEnd={handleAnimationEnd}>
        <Close onClick={handleCloseClick} data-testid="subtitleOptionsCloseBtn" />
        <OptionsSection>
          <OptionsGroup>
            <OptionsGroupHeader>{locale.translations.SubtitleOptions.FontSize}</OptionsGroupHeader>
            {textSizeOptions.map(textSizeOption => (
              <OptionsGroupItem
                key={textSizeOption}
                active={subtitleTextSize === textSizeOption}
                onClick={e => handleSizeClick(e, textSizeOption)}
                children={locale.translations.SubtitleOptions.TextSizeOption(textSizeOption)}
              />
            ))}
          </OptionsGroup>
          <OptionsGroup>
            <OptionsGroupHeader>{locale.translations.SubtitleOptions.FontColor}</OptionsGroupHeader>
            <ColorsGroup>
              {textColorOptions.map(colorOption => (
                <ColorOption
                  key={colorOption}
                  data-testid={`subtitleColorSwatch-${colorOption}`}
                  active={colorOption === subtitleTextColor}
                  color={colorOption}
                  onClick={() => handleColorClick(colorOption)}
                />
              ))}
            </ColorsGroup>
          </OptionsGroup>
        </OptionsSection>
        <BoldTextSection>
          <BoldTextHeader>{locale.translations.SubtitleOptions.BoldText}</BoldTextHeader>
          <Toggle
            active={subtitleTextWeight === SubtitleTextWeights.Bold}
            onClick={handleWeightChange}
            data-testid="subtitleTextBoldCheckbox"
          />
          <BoldLabel>
            {subtitleTextWeight === SubtitleTextWeights.Bold
              ? locale.translations.General.On
              : locale.translations.General.Off}
          </BoldLabel>
        </BoldTextSection>
      </Container>
    </>
  ) : null;
};

const hiddenAnimation = keyframes`
  0%    { opacity: 1;}
  99%   { opacity: 0;}
  100%  { opacity: 0;}
`;

const visibleAnimation = keyframes`
  0%    { opacity: 0; }
  100%  { opacity: 1; }
`;

const Container = styled.div`
  background-color: rgba(22, 24, 25, 0.7);
  border: solid 1px #fff;
  border-radius: 4px;
  position: absolute;
  bottom: 165px;
  right: 32px;
  padding: 32px 93px 32px 12px;
  display: flex;
  flex-direction: column;
  overflow: auto;

  &.hidden {
    animation: ${hiddenAnimation} 0.3s;
  }

  &.visible {
    animation: ${visibleAnimation} 0.3s;
  }

  @media ${theme.mobileMediaQuery} {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.bbBlack};
    border: 0;
    flex-direction: column;
    padding: 0px;
    padding-top: 42px;
    animation: none;
    z-index: 99999999;

    &.hidden {
      animation: none;
    }

    &.visible {
      animation: none;
    }
  }
`;

const Backdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

const Close = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 30px;
  height: 30px;
  background: url(${closeIcon}) center / contain no-repeat;
  cursor: pointer;

  @media ${theme.mobileMediaQuery} {
    top: 24px;
    left: 21px;
    right: initial;
    width: 18px;
    height: 18px;
  }
`;

const OptionsSection = styled.div`
  display: flex;

  @media ${theme.mobileMediaQuery} {
    flex-direction: column;
  }
`;

const OptionsGroup = styled.div`
  padding-left: 60px;

  @media ${theme.mobileMediaQuery} {
    padding-left: initial;
    padding: 32px 24px;
    border-bottom: 1px solid #fff;
  }
`;

const OptionsGroupHeader = styled.div`
  font-family: Lato;
  font-size: 18px;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: 0.4px;
  color: #f7f8fa;
  margin-top: 0px;
  margin-bottom: 8px;
  cursor: initial;
`;

const OptionsGroupItem = styled.div<{ active?: boolean }>`
  position: relative;
  font-family: Lato;
  font-size: 14px;
  font-stretch: normal;
  font-style: normal;
  line-height: 24px;
  letter-spacing: 0.3px;
  color: #fff;
  margin-top: 16px;
  cursor: pointer;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: ${({ theme }) => theme.primaryBlue};
    }
  }

  ${({ active }) =>
    active &&
    css`
      color: ${({ theme }) => theme.primaryBlue};
      &::before {
        content: '';
        position: absolute;
        width: 24px;
        height: 24px;
        background: url(${checkIcon}) center / contain no-repeat;
        left: -34px;

        @media ${theme.mobileMediaQuery} {
          left: calc(100% - 24px);
        }
      }
    `}
`;

const ColorsGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 32px);
  grid-template-rows: repeat(3, 32px);
  grid-gap: 16px;
  margin-top: 20px;

  @media ${theme.mobileMediaQuery} {
    display: flex;
    flex-wrap: wrap;
    grid-gap: 0px;
    margin-bottom: -16px;
  }
`;

const ColorOption = styled.div<{ active: boolean; color: string }>`
  width: 100%;
  height: 100%;
  background: ${({ color }) => color};
  border-radius: 4px;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.25);
  box-sizing: border-box;
  cursor: pointer;
  position: relative;

  ${({ active }) =>
    active &&
    css`
      border: solid 3px ${({ theme }) => theme.primaryBlue};
      &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: url(${checkIcon}) center / contain no-repeat;
      }
    `}
    
  @media ${theme.mobileMediaQuery} {
    width: 32px;
    height: 32px;
    margin: 0 16px 16px 0;
  }
`;

const BoldTextSection = styled.div`
  margin-top: 24px;
  padding-left: 60px;
  display: flex;
  align-items: center;

  @media ${theme.mobileMediaQuery} {
    padding-left: initial;
    padding: 32px 24px;
    margin-top: 2.5px;
  }
`;

const BoldTextHeader = styled(OptionsGroupHeader)`
  margin: 0;
  margin-right: 38px;
`;

const BoldLabel = styled.div`
  color: ${({ theme }) => theme.disabledGray};
  font-family: 'Lato-Bold', sans-serif;
  font-size: 14px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: 0.3px;
  margin-left: 11px;
`;
